import React, { useEffect, useState } from "react"
import ReactApexChart from "react-apexcharts"
import { Calendar } from "primereact/calendar"
import { gql, useMutation, useQuery } from "@apollo/client"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import "../../styles/misc.css"

const query = gql`
    query getBuyBoxHistory($productId: Long!, $shopId: Long!, $fromMillis: Long, $toMillis: Long) {
        buyBoxHistory(productId: $productId, shopId: $shopId, fromMillis: $fromMillis, toMillis: $toMillis) {
            hasRecommendedPrice
            points {
                crawlDate
                buyboxProvider
                buyboxOwnerPrice
                buyboxOwnerTotalPrice
                recommendedBuyboxPrice
            }
        }
    }
`

const START_BUYBOX_HISTORY_EXPORT = gql`
    mutation startBuyBoxHistoryExport(
        $productId: Long!
        $shopId: Long!
        $fromMillis: Long
        $toMillis: Long
        $exportType: String!
        $useTotalPrice: Boolean
    ) {
        startBuyBoxHistoryExport(
            productId: $productId
            shopId: $shopId
            fromMillis: $fromMillis
            toMillis: $toMillis
            exportType: $exportType
            useTotalPrice: $useTotalPrice
        ) {
            responseCode
            responseMessage
            downloadUrl
        }
    }
`

function createDefaultDateRange() {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 2 * 7)
    startDate.setHours(0, 0, 0, 0)

    const endDate = new Date()
    endDate.setHours(23, 59, 59, 999)

    return [startDate, endDate]
}

function prepareEndDate(endDate) {
    if (!endDate) {
        return endDate
    }
    const prepared = new Date(endDate)
    prepared.setHours(23, 59, 59, 999)
    return prepared
}

function PPDateRangePicker({ value, onChange }) {
    return (
        <Calendar
            selectionMode="range"
            value={value}
            dateFormat="dd.mm.yy"
            showIcon
            selectOtherMonths
            showButtonBar
            readOnlyInput
            appendTo={document.body}
            baseZIndex={2000}
            onChange={(e) => {
                const next = e.value
                if (!Array.isArray(next)) {
                    return
                }
                if (next[0] && next[1]) {
                    onChange([next[0], prepareEndDate(next[1])])
                    return
                }
                onChange(next)
            }}
        />
    )
}

function buildTooltipHtml(t, seriesName, data) {
    let html = '<div class="apexcharts-tooltip-title">'
    html +=
        new Date(data[0]).toLocaleDateString("de-DE", {
            year: "numeric",
            month: "2-digit",
            day: "numeric"
        }) +
        " " +
        new Date(data[0]).toLocaleTimeString("de-DE", { hour: "numeric", minute: "numeric" })
    html += "</div>"
    html += "<ul class='apexchart_custom_list'>"
    html += "<li><b>" + seriesName + "</b></li>"
    if (data[1] != null && data[1] !== "") {
        html += "<li><b>" + t("single.price") + "</b>: " + data[1] + "</li>"
    }
    if (data[2] && data[2] !== "") {
        html += "<li><b>" + t("single.dealers") + "</b>: " + data[2] + "</li>"
    }
    html += "</ul>"
    return html
}

function prepareChartOptions(t, titleLines, seriesColor) {
    return {
        colors: [seriesColor || "#ff9900"],
        chart: {
            height: 250,
            type: "area",
            toolbar: {
                show: true
            },
            animations: {
                enabled: false
            }
        },
        title: {
            text: titleLines
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: "smooth"
        },
        xaxis: {
            type: "datetime",
            tooltip: {
                enabled: false
            }
        },
        yaxis: {
            type: "numeric"
        },
        tooltip: {
            custom: function ({ seriesIndex, dataPointIndex, w }) {
                let data = w.globals.initialSeries[seriesIndex].data[dataPointIndex]
                let name = w.globals.initialSeries[seriesIndex].name
                return buildTooltipHtml(t, name, data)
            }
        }
    }
}

function PPBuyBoxHistory(props) {
    const [t] = useTranslation()
    const [dateRange, setDateRange] = useState(createDefaultDateRange)
    const [variables, setVariables] = useState()

    function handleDateChange(nextRange) {
        setDateRange(nextRange)
    }

    const { data, loading, error } = useQuery(query, {
        displayName: "BuyBoxHistoryQuery",
        variables: variables,
        fetchPolicy: "network-only",
        skip: !variables?.productId || !variables?.shopId || !variables?.fromMillis || !variables?.toMillis
    })

    useEffect(() => {
        if (error) console.error(error)
    }, [error])

    const history = data?.buyBoxHistory || { points: [], hasRecommendedPrice: false }

    const [triggerBuyBoxHistoryExport] = useMutation(START_BUYBOX_HISTORY_EXPORT, {
        fetchPolicy: "no-cache",
        onCompleted: () => {
            toast(t("export.started"))
        },
        onError: (error) => {
            console.error(error)
            toast.error(t("export.start_failed"))
        }
    })

    function startExport(exportType) {
        if (!props.selectedProductId || props.shopId == null) {
            return
        }
        triggerBuyBoxHistoryExport({
            variables: {
                productId: props.selectedProductId,
                shopId: props.shopId,
                fromMillis: dateRange?.[0]?.getTime?.() ?? null,
                toMillis: dateRange?.[1]?.getTime?.() ?? null,
                exportType,
                useTotalPrice: localStorage.getItem("showTotalPrice") === "true"
            }
        })
    }

    useEffect(() => {
        if (props.selectedProductId === undefined || props.shopId === undefined || props.shopId === null) {
            return
        }
        if (!dateRange?.[0] || !dateRange?.[1]) {
            return
        }

        setVariables({
            productId: props.selectedProductId,
            shopId: props.shopId,
            fromMillis: dateRange[0].getTime(),
            toMillis: dateRange[1].getTime()
        })
    }, [props.selectedProductId, props.shopId, dateRange])

    const productname = (props?.productname || props?.foreignShop?.productName || "")
        .toString()
        .replace(/^(.{90}[^\s]*).*/, "$1")

    const useTotalPrice = localStorage.getItem("showTotalPrice") === "true"
    const chartKey = `${variables?.fromMillis || 0}-${variables?.toMillis || 0}-${(history.points || []).length}-${useTotalPrice}`

    function getOwnerPrice(point) {
        return useTotalPrice ? point.buyboxOwnerTotalPrice : point.buyboxOwnerPrice
    }

    const ownerSeries = [
        {
            name: t("pricetable.righttab.buybox_history.owner_price"),
            data: (history.points || [])
                .filter((p) => getOwnerPrice(p) != null)
                .map((p) => [p.crawlDate, Number(getOwnerPrice(p)).toFixed(2), p.buyboxProvider || ""])
        }
    ]

    const recommendedSeries = [
        {
            name: t("pricetable.righttab.buybox_history.recommended_price"),
            data: (history.points || [])
                .filter((p) => p.recommendedBuyboxPrice != null)
                .map((p) => [
                    p.crawlDate,
                    Number(p.recommendedBuyboxPrice).toFixed(2),
                    t("pricetable.righttab.buybox_history.recommended_price")
                ])
        }
    ]

    const hasOwnerData = ownerSeries[0].data.length > 0
    const hasRecommendedData = history.hasRecommendedPrice && recommendedSeries[0].data.length > 0

    return (
        <div>
            <PPDateRangePicker value={dateRange} onChange={handleDateChange} />

            {loading && <p>{t("single.loading", "Laden...")}</p>}

            {!loading && hasOwnerData && (
                <>
                    <ReactApexChart
                        key={`owner-${chartKey}`}
                        options={prepareChartOptions(
                            t,
                            [
                                t("pricetable.righttab.buybox_history.owner_price") + ": " + (props?.productNr || ""),
                                productname
                            ],
                            "#ff9900"
                        )}
                        series={ownerSeries}
                        type="area"
                        width={"100%"}
                        height={250}
                    />
                    <button
                        type="button"
                        className="btn w-100 btn-primary mb-3"
                        onClick={() => startExport("OWNER")}>
                        {t("pricetable.righttab.buybox_history.export_owner")}
                    </button>
                </>
            )}

            {!loading && hasRecommendedData && (
                <>
                    <ReactApexChart
                        key={`recommended-${chartKey}`}
                        options={prepareChartOptions(
                            t,
                            [
                                t("pricetable.righttab.buybox_history.recommended_price") +
                                    ": " +
                                    (props?.productNr || ""),
                                productname
                            ],
                            "#232f3e"
                        )}
                        series={recommendedSeries}
                        type="area"
                        width={"100%"}
                        height={250}
                    />
                    <button
                        type="button"
                        className="btn w-100 btn-primary mb-3"
                        onClick={() => startExport("RECOMMENDED")}>
                        {t("pricetable.righttab.buybox_history.export_recommended")}
                    </button>
                </>
            )}

            {!loading && !hasOwnerData && !hasRecommendedData && (
                <p>
                    {t("pricetable.righttab.buybox_history.no_history_available_1")}
                    <br />
                    {t("pricetable.righttab.buybox_history.no_history_available_2")}
                </p>
            )}
        </div>
    )
}

export default PPBuyBoxHistory
